/**
 * domains/journal/calibration/attribution.ts — MER Algo Footprints v7.1H
 *
 * Deterministic attribution classifier — canonical logic path.
 *
 * Field resolution priority:
 *   1. setup2 rich fields (setup2_score, is_actionable, is_no_valid_setup, penalties, disqualifiers)
 *   2. thesis2 rich fields (thesis_state, action_posture)
 *   3. Fallback to AuditRecord generic fields (confidence, status, outcome)
 *
 * This is the SINGLE AUTHORITATIVE attribution logic. The server
 * server/learning/outcomeEvaluator.js mirrors this decision tree for
 * runtime classification; these two stay in sync through shared constants.
 */

import { AuditRecord } from '../../../shared/types';
import {
  AttributionCategory, SignalQuality, ExecutionQuality, EngineAlignment,
  AttributedRecord, CalibrationCandidateKind,
} from './types';

// ─── Thresholds — mirrors setup2 DEFAULT_THRESHOLDS ──────────────────────────
export const MIN_SETUP_SCORE  = 65;
export const MIN_READINESS    = 70;
export const NEAR_MISS_DELTA  = 10;
export const STRONG_WIN_R     = 1.5;
export const CLEAR_LOSS_R     = -0.8;

// ─── Field resolution ─────────────────────────────────────────────────────────

export function getSetupScore(r: AuditRecord): number {
  return r.setup2_score ?? r.confidence;
}

export function getReadinessScore(r: AuditRecord): number {
  return r.setup2_readiness ?? 0;
}

export function resolveIsActionable(r: AuditRecord): boolean {
  if (r.is_actionable != null) return r.is_actionable;
  if (r.setup2_state === 'READY' || r.setup2_state === 'ACTIVE') return true;
  if (r.setup2_state === 'QUALIFIED' || r.setup2_state === 'DEVELOPING') return false;
  return r.confidence >= MIN_SETUP_SCORE;
}

export function resolveIsNoValidSetup(r: AuditRecord): boolean {
  return r.is_no_valid_setup === true || r.setup2_classification === 'NO_VALID_SETUP';
}

export function getPenalties(r: AuditRecord): Array<{ reason: string; points: number }> {
  return r.setup2_penalties ?? [];
}

export function getDisqualifiers(r: AuditRecord): string[] {
  return r.setup2_disqualifiers ?? [];
}

// ─── Outcome helpers ──────────────────────────────────────────────────────────

function isWin(r: AuditRecord): boolean {
  return r.outcome === 'win' || (r.realizedR !== null && r.realizedR > 0);
}

function isLoss(r: AuditRecord): boolean {
  return r.outcome === 'loss' || (r.realizedR !== null && r.realizedR < 0);
}

function isCompleted(r: AuditRecord): boolean {
  return r.outcome === 'win' || r.outcome === 'loss' || r.outcome === 'breakeven';
}

function isTaken(r: AuditRecord): boolean {
  return r.status === 'closed' && r.exitPrice !== null;
}

function isSkipped(r: AuditRecord): boolean {
  return r.status === 'skipped';
}

// ─── Attribution category ─────────────────────────────────────────────────────

function classifyCategory(r: AuditRecord): { category: AttributionCategory; reason: string } {
  const score      = getSetupScore(r);
  const actionable = resolveIsActionable(r);
  const nvs        = resolveIsNoValidSetup(r);
  const taken      = isTaken(r);
  const skipped    = isSkipped(r);
  const completed  = isCompleted(r);
  const win        = isWin(r);
  const loss       = isLoss(r);
  const rr         = r.realizedR;
  const disqs      = getDisqualifiers(r);

  // NO_VALID_SETUP path — explicit evaluated no-trade outcome
  if (nvs && completed) {
    if (loss) return { category: 'CORRECT_SUPPRESSION', reason: `NO_VALID_SETUP correctly predicted no-trade (R=${rr?.toFixed(2)})` };
    if (win)  return { category: 'FALSE_NEGATIVE',      reason: `NO_VALID_SETUP emitted but market moved favorably (R=${rr?.toFixed(2)}) — suppression may be miscalibrated` };
    return    { category: 'NEUTRAL', reason: 'NO_VALID_SETUP — breakeven outcome' };
  }

  if (actionable && taken && completed) {
    if (win)  return { category: 'CORRECT_TRADE',  reason: `Actionable setup (score=${score}) → win (R=${rr?.toFixed(2)})` };
    if (loss) return { category: 'FALSE_POSITIVE', reason: `Actionable setup (score=${score}) → loss (R=${rr?.toFixed(2)})` };
    return    { category: 'NEUTRAL', reason: 'Breakeven — inconclusive' };
  }

  if (actionable && skipped && completed) {
    if (win)  return { category: 'MISSED_WIN',   reason: `Actionable (score=${score}) but skipped → would-have-won (R=${rr?.toFixed(2)})` };
    if (loss) return { category: 'AVOIDED_LOSS', reason: `Actionable (score=${score}) but skipped → avoided loss (R=${rr?.toFixed(2)})` };
    return    { category: 'NEUTRAL', reason: 'Skipped breakeven' };
  }

  if (!actionable && completed) {
    const why = disqs.length ? `disqualified by [${disqs.join(', ')}]` : `score=${score} < ${MIN_SETUP_SCORE}`;
    if (win)  return { category: 'FALSE_NEGATIVE', reason: `Filtered (${why}) → would-have-won (R=${rr?.toFixed(2)})` };
    if (loss) return { category: 'GOOD_FILTER',    reason: `Filtered (${why}) → would-have-lost (R=${rr?.toFixed(2)})` };
    return    { category: 'NEUTRAL', reason: 'Filtered breakeven' };
  }

  if (r.status === 'invalidated') return { category: 'NEUTRAL', reason: 'Invalidated before outcome' };
  return { category: 'NEUTRAL', reason: 'Insufficient data' };
}

// ─── Signal quality — prefers setup2 composite when available ────────────────

function classifySignalQuality(r: AuditRecord): SignalQuality {
  if (r.setup2_score != null && r.setup2_readiness != null) {
    const combined = r.setup2_score * 0.6 + r.setup2_readiness * 0.4;
    if (combined >= 80) return 'EXCELLENT';
    if (combined >= 65) return 'GOOD';
    if (combined >= 50) return 'MIXED';
    if (combined >= 35) return 'WEAK';
    return 'MISLEADING';
  }
  // Fallback: structural signal composite
  const smcAligned   = r.smcBias !== 'neutral' && r.smcBias !== 'mixed';
  const dispAligned  = r.displacementDirection === (r.direction === 'long' ? 'bullish' : 'bearish');
  const trendAligned = r.trendAlignment === 'aligned_with_trend';
  const inTrend      = r.trendState === 'trend' || r.trendState === 'pullback';
  const score =
    (r.confidence >= 75 ? 3 : r.confidence >= 60 ? 2 : r.confidence >= 45 ? 1 : 0) +
    (smcAligned ? 2 : 0) + (dispAligned ? 1 : 0) + (trendAligned && inTrend ? 2 : 0);
  if (score >= 7) return 'EXCELLENT';
  if (score >= 5) return 'GOOD';
  if (score >= 3) return 'MIXED';
  return isLoss(r) && resolveIsActionable(r) ? 'MISLEADING' : 'WEAK';
}

// ─── Execution quality ────────────────────────────────────────────────────────

function classifyExecutionQuality(r: AuditRecord): ExecutionQuality {
  if (isSkipped(r) || r.status === 'invalidated') return 'NOT_APPLICABLE';
  if (!isTaken(r)) return 'MISSED';
  const mfe = r.mfe ?? 0;
  const mae = r.mae ?? 0;
  if (mae < -0.5 && mfe < 0.3) return 'EARLY';
  if (mfe < 0.5 && mae >= -0.2 && isLoss(r)) return 'LATE';
  return 'DISCIPLINED';
}

// ─── Engine alignment — prefers setup2+thesis2 three-layer check ─────────────

function classifyEngineAlignment(r: AuditRecord): EngineAlignment {
  if (r.setup2_state != null || r.thesis_state != null) {
    const setupOk   = r.setup2_state === 'READY' || r.setup2_state === 'ACTIVE' || r.setup2_state === 'DEVELOPING';
    const thesisOk  = r.thesis_state != null && !['WAIT', 'HIGH_RISK', 'AVOID'].includes(r.thesis_state);
    const postureOk = r.action_posture != null && !['NO_TRADE', 'NEUTRAL'].includes(r.action_posture);
    const count = [setupOk, thesisOk, postureOk].filter(Boolean).length;
    if (count === 3) return 'STRONG';
    if (count >= 2)  return 'PARTIAL';
    return 'MISALIGNED';
  }
  // Fallback: structural alignment
  const smcDir  = r.smcBias === 'bullish' ? 'long' : r.smcBias === 'bearish' ? 'short' : null;
  const dispDir = r.displacementDirection === 'bullish' ? 'long' : r.displacementDirection === 'bearish' ? 'short' : null;
  const count   = [smcDir === r.direction, dispDir === r.direction, r.trendAlignment === 'aligned_with_trend'].filter(Boolean).length;
  if (count === 3) return 'STRONG';
  if (count >= 2)  return 'PARTIAL';
  return 'MISALIGNED';
}

// ─── Calibration candidate detection — uses real setup2 thresholds ────────────

function detectCalibrationKind(r: AuditRecord): {
  is_candidate:   boolean;
  kind?:          CalibrationCandidateKind;
  reason?:        string;
  delta?:         number;
  threshold?:     number;
  current_value?: number;
} {
  const score     = getSetupScore(r);
  const rdy       = getReadinessScore(r);
  const rr        = r.realizedR;
  const win       = isWin(r);
  const loss      = isLoss(r);
  const completed = isCompleted(r);
  const disqs     = getDisqualifiers(r);
  const penalties = getPenalties(r);

  if (!completed) return { is_candidate: false };

  const belowThreshold = !resolveIsActionable(r);
  const nearMissLow    = belowThreshold && (MIN_SETUP_SCORE - score) <= NEAR_MISS_DELTA && score > 0;
  if (nearMissLow && win && rr !== null && rr >= STRONG_WIN_R) {
    return {
      is_candidate: true, kind: 'THRESHOLD_MISS_LOWER',
      reason: `Score ${score} just below threshold ${MIN_SETUP_SCORE} (Δ=${MIN_SETUP_SCORE - score}) — strong win (R=${rr.toFixed(2)})${disqs.length ? ` | disqualified by [${disqs.join(', ')}]` : ''}`,
      delta: MIN_SETUP_SCORE - score, threshold: MIN_SETUP_SCORE, current_value: score,
    };
  }

  const rdyNearMiss = rdy > 0 && rdy < MIN_READINESS && (MIN_READINESS - rdy) <= NEAR_MISS_DELTA;
  if (rdyNearMiss && win && rr !== null && rr >= STRONG_WIN_R && !belowThreshold) {
    return {
      is_candidate: true, kind: 'READINESS_MISS_LOWER',
      reason: `Readiness ${rdy} just below threshold ${MIN_READINESS} (Δ=${MIN_READINESS - rdy}) — strong win (R=${rr.toFixed(2)}) missed`,
      delta: MIN_READINESS - rdy, threshold: MIN_READINESS, current_value: rdy,
    };
  }

  const nearMissUpper = resolveIsActionable(r) && (score - MIN_SETUP_SCORE) <= NEAR_MISS_DELTA;
  if (nearMissUpper && loss && rr !== null && rr <= CLEAR_LOSS_R) {
    return {
      is_candidate: true, kind: 'THRESHOLD_MISS_UPPER',
      reason: `Score ${score} just above threshold ${MIN_SETUP_SCORE} (Δ=${score - MIN_SETUP_SCORE}) — decisive loss (R=${rr.toFixed(2)})${penalties.length ? ` | penalties: [${penalties.map(p => p.reason).join(', ')}]` : ''}`,
      delta: score - MIN_SETUP_SCORE, threshold: MIN_SETUP_SCORE, current_value: score,
    };
  }

  if (resolveIsActionable(r) && isTaken(r) && loss && score >= 75 && rr !== null && rr <= CLEAR_LOSS_R) {
    return {
      is_candidate: true, kind: 'FALSE_POSITIVE_PATTERN',
      reason: `High-score setup (${score}) failed decisively (R=${rr.toFixed(2)}) — ${r.regime} regime${penalties.length ? ` | active penalties: [${penalties.map(p => p.reason).join(', ')}]` : ''}`,
      delta: 0, threshold: MIN_SETUP_SCORE, current_value: score,
    };
  }

  if (!resolveIsActionable(r) && win && rr !== null && rr >= STRONG_WIN_R * 1.5) {
    return {
      is_candidate: true, kind: 'FALSE_NEGATIVE_PATTERN',
      reason: `Filtered setup (score=${score}) missed strong winner (R=${rr.toFixed(2)})${disqs.length ? ` | disqualifiers: [${disqs.join(', ')}]` : ''}`,
      delta: MIN_SETUP_SCORE - score, threshold: MIN_SETUP_SCORE, current_value: score,
    };
  }

  return { is_candidate: false };
}

// ─── Main classifier ──────────────────────────────────────────────────────────

export function classifyRecord(r: AuditRecord): AttributedRecord {
  const { category, reason: category_reason } = classifyCategory(r);
  const cal = detectCalibrationKind(r);

  return {
    audit_id:         r.id,
    symbol:           r.symbol,
    setup_type:       r.setup2_classification ?? r.setupType,
    regime:           r.regime,
    confidence:       r.confidence,
    setup2_score:     r.setup2_score ?? null,
    setup2_readiness: r.setup2_readiness ?? null,
    is_actionable:    resolveIsActionable(r),
    is_no_valid_setup: resolveIsNoValidSetup(r),
    penalties:        getPenalties(r),
    disqualifiers:    getDisqualifiers(r),
    thesis_state:     r.thesis_state ?? null,
    action_posture:   r.action_posture ?? null,
    rank_bucket:      r.rank_bucket ?? null,
    rank_score:       r.rank_score ?? null,
    readiness:        r.readiness,
    direction:        r.direction,
    outcome:          r.outcome,
    realized_r:       r.realizedR,
    mfe:              r.mfe,
    mae:              r.mae,

    category,
    category_reason,
    signal_quality:    classifySignalQuality(r),
    execution_quality: classifyExecutionQuality(r),
    engine_alignment:  classifyEngineAlignment(r),

    is_calibration_candidate: cal.is_candidate,
    ...(cal.is_candidate ? { calibration_kind: cal.kind, calibration_reason: cal.reason } : {}),
  };
}

export function classifyRecords(records: AuditRecord[]): AttributedRecord[] {
  return records.map(classifyRecord);
}
